import React from 'react';
import { LogEntry, LogLocation, LogFilter, TaskTab } from '../types';
import { LogList } from './LogList';

type ConsolePanelProps = {
  logs: LogEntry[];
  logFilter: LogFilter;
  taskTab: TaskTab;
  taskRunning: string | null;
  hasTaskEndpoint: boolean;
  onLint: () => void;
  onTest: () => void;
  onBuild: () => void;
  getLocationFromLog: (log: LogEntry) => LogLocation | null;
  onNavigateLocation: (loc: LogLocation) => void;
  onChangeFilter: (value: LogFilter) => void;
  onChangeTaskTab: (tab: TaskTab) => void;
  onClearTaskLogs: (task: TaskTab) => void;
};

const TASK_TABS: { id: TaskTab; label: string; icon: string }[] = [
  { id: 'all', label: 'All', icon: '...' },
  { id: 'lint', label: 'Lint', icon: '!' },
  { id: 'test', label: 'Test', icon: 't' },
  { id: 'build', label: 'Build', icon: 'b' },
  { id: 'preview', label: 'Preview', icon: 'p' },
];

export function ConsolePanel({
  logs,
  logFilter,
  taskTab,
  taskRunning,
  hasTaskEndpoint,
  onLint,
  onTest,
  onBuild,
  getLocationFromLog,
  onNavigateLocation,
  onChangeFilter,
  onChangeTaskTab,
  onClearTaskLogs,
}: ConsolePanelProps) {
  return (
    <>
      <div className="console-toolbar">
        <div className="cta-group">
          <button className="micro" onClick={onLint} disabled={!!taskRunning}>
            Lint
          </button>
          <button className="micro" onClick={onTest} disabled={!!taskRunning}>
            Test
          </button>
          <button className="micro" onClick={onBuild} disabled={!!taskRunning}>
            Build
          </button>
        </div>
        <button
          className="micro subtle"
          onClick={() => onClearTaskLogs(taskTab)}
          title={`Clear ${taskTab === 'all' ? 'all' : taskTab} logs`}
        >
          Clear
        </button>
      </div>

      {/* Task output tabs */}
      <div className="task-tabs">
        {TASK_TABS.map((tab) => (
          <button
            key={tab.id}
            className={`task-tab ${taskTab === tab.id ? 'active' : ''}`}
            onClick={() => onChangeTaskTab(tab.id)}
            title={tab.label}
          >
            <span className="task-tab-icon">{tab.icon}</span>
            <span className="task-tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="log-filters">
        {(['all', 'info', 'warn', 'error', 'success'] as const).map((f) => (
          <button
            key={f}
            className={`pill small ${logFilter === f ? 'success' : 'subtle'}`}
            onClick={() => onChangeFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>
      {!hasTaskEndpoint && (
        <p className="muted">Set REACT_APP_TASK_ENDPOINT or implement /api/run-task to run lint/test/build.</p>
      )}
      <LogList logs={logs} getLocationFromLog={getLocationFromLog} onNavigateLocation={onNavigateLocation} />
    </>
  );
}

export default React.memo(ConsolePanel);
